/**
 * Donnees locales du poste pour les ecrans (catalogue en place, employes,
 * etat des synchros, confirmation d'un responsable) et lancement des synchros.
 *
 * Une seule synchro a la fois : un deuxieme appel pendant qu'une tourne
 * attend la meme.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getCatalogMeta } from '../store/catalog';
import { listEmployees } from '../store/employees';
import { getMetaMany } from '../store/meta';
import { META_KEYS } from '../store/schema';
import { syncAll } from '../sync/syncAll';

const EMPTY = Object.freeze({
    loaded: false,
    catalogMeta: null,
    employees: [],
    catalogSync: null,
    rosterSync: null,
    managerConfirmation: null,
});

export function useCaisseSync({ db, api, token, storeId }) {
    const [data, setData] = useState(EMPTY);
    const [syncing, setSyncing] = useState(false);
    const [progress, setProgress] = useState(null);
    const running = useRef(null);

    const reload = useCallback(async () => {
        if (!db) return EMPTY;
        const [catalogMeta, employees, meta] = await Promise.all([
            getCatalogMeta(db),
            listEmployees(db),
            getMetaMany(db, [META_KEYS.CATALOG_SYNC, META_KEYS.ROSTER_SYNC, META_KEYS.MANAGER_CONFIRMATION]),
        ]);
        const next = {
            loaded: true,
            catalogMeta,
            employees,
            catalogSync: meta[META_KEYS.CATALOG_SYNC] || null,
            rosterSync: meta[META_KEYS.ROSTER_SYNC] || null,
            managerConfirmation: meta[META_KEYS.MANAGER_CONFIRMATION] || null,
        };
        setData(next);
        return next;
    }, [db]);

    useEffect(() => {
        reload().catch(() => {});
    }, [reload]);

    /**
     * @param {{ catalog?: boolean }} [options]
     * @returns {Promise<{ catalog: object|null, roster: object|null, terminalStatus: string|null, data: object|null } | null>}
     */
    const runSync = useCallback(async ({ catalog = true } = {}) => {
        if (!db || !token) return null;
        if (running.current) return running.current;
        setSyncing(true);
        setProgress({ step: 'config', percent: 0 });
        const pending = (async () => {
            let result;
            try {
                result = await syncAll({ db, api, token, storeId, catalog, onProgress: setProgress });
            } catch (_error) {
                result = { catalog: { ok: false, code: 'ERREUR' }, roster: null, terminalStatus: null };
            }
            // `data` : l'etat relu apres la synchro, pour l'appelant.
            const data = await reload().catch(() => null);
            setSyncing(false);
            running.current = null;
            return { ...result, data };
        })();
        running.current = pending;
        return pending;
    }, [db, api, token, storeId, reload]);

    return { ...data, syncing, progress, reload, runSync };
}
